import { useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import api from "../../lib/api";
import { Card } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

const LANGUAGES = [
  { id: "rw", label: "Kinyarwanda" },
  { id: "en", label: "English" },
  { id: "fr", label: "French" },
];

const PERIODS = [7, 30, 90];

const SOURCE_NAMES = {
  google: "Google",
  bing: "Bing",
  "other-search": "Other search engines",
  social: "Social media and WhatsApp",
  referral: "Links on other websites",
  direct: "Came straight to SHUWA",
};

const selectClass = "h-11 rounded-xl border border-border bg-white px-3 text-sm text-slate-900 outline-none focus:border-primary";

function TermTable({ title, hint, rows, column, onTeach }) {
  return (
    <Card className="p-4">
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="mb-3 text-xs text-muted-foreground">{hint}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing yet.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((row) => (
            <li key={row.term} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 break-words text-slate-900">{row.term}</span>
              <span className="flex shrink-0 items-center gap-2">
                {onTeach && !row.understood && (
                  <button type="button" onClick={() => onTeach(row.term)} className="text-xs font-semibold text-primary hover:underline">
                    Teach
                  </button>
                )}
                <span className="text-muted-foreground">
                  {row[column]}
                  {column !== "searches" && ` of ${row.searches}`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Admin → Search: the words customers search with and the category each
 * one means, next to what customers actually searched for, so the words
 * worth adding are easy to spot.
 */
export default function Search() {
  const [categories, setCategories] = useState([]);
  const [synonyms, setSynonyms] = useState([]);
  const [insights, setInsights] = useState(null);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ term: "", category_slug: "", language: "rw" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadSynonyms = () =>
    api.get("/admin/search/synonyms").then(({ data }) => {
      setCategories(data.categories || []);
      setSynonyms(data.synonyms || []);
      setForm((f) => ({ ...f, category_slug: f.category_slug || data.categories?.[0]?.slug || "" }));
    });

  useEffect(() => {
    loadSynonyms().catch(() => setError("Couldn't load the search words. Please refresh."));
  }, []);

  useEffect(() => {
    api
      .get("/admin/search/insights", { params: { days } })
      .then(({ data }) => setInsights(data))
      .catch(() => setInsights(null));
  }, [days]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post("/admin/search/synonyms", form);
      const added = data.created.map((s) => s.term);
      setNotice(
        [
          added.length > 0 && `Added ${added.join(", ")}.`,
          data.already_known.length > 0 && `Already known: ${data.already_known.join(", ")}.`,
        ]
          .filter(Boolean)
          .join(" ")
      );
      setForm((f) => ({ ...f, term: "" }));
      await loadSynonyms();
    } catch (err) {
      setError(err?.response?.data?.errors?.term?.[0] || err?.response?.data?.message || "Couldn't add that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const update = async (synonym, changes) => {
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.put(`/admin/search/synonyms/${synonym.id}`, changes);
      setSynonyms((list) => list.map((s) => (s.id === synonym.id ? data : s)));
    } catch (err) {
      setError(err?.response?.data?.errors?.term?.[0] || err?.response?.data?.message || "Couldn't save that change.");
    }
  };

  const rename = (synonym) => {
    const term = window.prompt(`Change “${synonym.term}” to:`, synonym.term);
    if (term && term.trim() && term.trim() !== synonym.term) update(synonym, { term });
  };

  const remove = async (synonym) => {
    if (!window.confirm(`Remove “${synonym.term}”? Searching it will no longer list ${categoryName(synonym.category_slug)}.`)) return;
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/admin/search/synonyms/${synonym.id}`);
      setSynonyms((list) => list.filter((s) => s.id !== synonym.id));
    } catch {
      setError("Couldn't remove that word. Please try again.");
    }
  };

  const categoryName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  // Words grouped by the category they mean; words for a category that no
  // longer exists are still listed, so they can be moved or removed.
  const groups = useMemo(() => {
    const wanted = filter.trim().toLowerCase();
    const slugs = [...new Set([...categories.map((c) => c.slug), ...synonyms.map((s) => s.category_slug)])];

    return slugs
      .map((slug) => ({
        slug,
        words: synonyms.filter((s) => s.category_slug === slug && (!wanted || s.term.includes(wanted))),
      }))
      .filter((group) => group.words.length > 0 || !wanted);
  }, [categories, synonyms, filter]);

  const teach = (term) => {
    setForm((f) => ({ ...f, term: term.split(" ").join(", ") }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <h1 className="mb-1 font-montserrat text-2xl font-bold text-slate-900">Search</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        The words customers search with, in any language, and the kind of business each one means. Searching a word
        lists every business in its category, whatever language the business is listed in.
      </p>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
      {notice && <p className="mb-4 text-sm text-emerald-700">{notice}</p>}

      <Card className="mb-6 p-4">
        <form onSubmit={add} className="flex flex-wrap items-end gap-3">
          <label className="min-w-0 flex-1 basis-56 text-sm font-medium text-slate-900">
            Word
            <Input
              value={form.term}
              onChange={(e) => setForm({ ...form, term: e.target.value })}
              placeholder="inzara, urwara"
              className="mt-1 w-full"
              required
            />
          </label>
          <label className="text-sm font-medium text-slate-900">
            Means
            <select
              value={form.category_slug}
              onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
              className={`mt-1 block ${selectClass}`}
              required
            >
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-900">
            Language
            <select
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className={`mt-1 block ${selectClass}`}
            >
              {LANGUAGES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={saving || !form.category_slug}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">
          Single words, separated by commas. Capital letters and accents don't matter, and a word with one letter
          wrong is still understood, so common misspellings rarely need adding.
        </p>
      </Card>

      <div className="mb-8">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-montserrat text-lg font-semibold text-slate-900">What customers searched for</h2>
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  days === p ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-muted"
                }`}
              >
                {p} days
              </button>
            ))}
          </div>
        </div>

        {!insights ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-3 gap-3">
              {[
                ["Searches", insights.totals.searches],
                ["Found nothing", insights.totals.zero_results],
                ["Led to a booking", insights.totals.bookings],
              ].map(([label, value]) => (
                <Card key={label} className="p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="font-montserrat text-2xl font-bold text-slate-900">{value.toLocaleString()}</p>
                </Card>
              ))}
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <TermTable
                title="Found nothing"
                hint="Searches with no results. “Teach” adds the words to the dictionary."
                rows={insights.zero_results}
                column="zero_results"
                onTeach={teach}
              />
              <TermTable
                title="Led to bookings"
                hint="Searches after which a customer booked."
                rows={insights.bookings}
                column="bookings"
              />
              <TermTable title="Most searched" hint="Every search, most common first." rows={insights.top} column="searches" onTeach={teach} />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <Card className="p-4">
                <p className="font-semibold text-slate-900">Where bookings came from</p>
                <p className="mb-3 text-xs text-muted-foreground">How the visit that ended in a booking started (website only).</p>
                {insights.sources.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {insights.sources.map((row) => (
                      <li key={row.source} className="flex items-center justify-between gap-3 py-2">
                        <span className="text-slate-900">{SOURCE_NAMES[row.source] || row.source}</span>
                        <span className="text-muted-foreground">{row.bookings}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-4 lg:col-span-2">
                <p className="font-semibold text-slate-900">Pages that brought bookings</p>
                <p className="mb-3 text-xs text-muted-foreground">The first page of those visits, and where the visitor came from.</p>
                {insights.landing_pages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {insights.landing_pages.map((row) => (
                      <li key={`${row.source} ${row.landing_page}`} className="flex items-center justify-between gap-3 py-2">
                        <span className="min-w-0 break-all text-slate-900">{row.landing_page}</span>
                        <span className="shrink-0 text-muted-foreground">
                          {SOURCE_NAMES[row.source] || row.source} · {row.bookings}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Only daily totals are kept — not who searched or booked. Searches that look like a phone number or an
              email address are never counted.
            </p>
          </>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-montserrat text-lg font-semibold text-slate-900">Words the search knows ({synonyms.length})</h2>
        <Input placeholder="Find a word…" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:max-w-xs" />
      </div>

      <div className="space-y-3">
        {groups.map(({ slug, words }) => (
          <Card key={slug} className="p-4">
            <p className="mb-2 font-semibold text-slate-900">{categoryName(slug)}</p>
            {words.length === 0 ? (
              <p className="text-sm text-muted-foreground">No words yet — only the category's own name finds it.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {words.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white py-1 pl-3 pr-1.5 text-sm">
                    <button type="button" onClick={() => rename(s)} title="Change this word" className="text-slate-900 hover:text-primary">
                      {s.term}
                    </button>
                    <select
                      value={s.language || ""}
                      onChange={(e) => update(s, { language: e.target.value || null })}
                      aria-label={`Language of ${s.term}`}
                      className="bg-transparent text-xs uppercase text-muted-foreground outline-none"
                    >
                      <option value="">—</option>
                      {LANGUAGES.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.id}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => remove(s)}
                      aria-label={`Remove ${s.term}`}
                      className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Click a word to change it. Up to four Kinyarwanda words of a category are also shown on its public
        page, so customers see the names they search with.
      </p>
    </div>
  );
}
